// Office "Engineers" page (admin only). Lists engineer accounts (techId, name,
// email, active/inactive), adds new ones (display name, email, mobile -> the
// server allocates the next TECH-NNN and creates the linked Engineer record), and
// Session BCE: engineers have NO password and no username to type. They sign in on the app with their email and an
// emailed 6-digit code, so email and mobile are both required, and "Send app details" leaves a Gmail draft telling them
// where the app is and how to sign in. The old Reset password, Send login and Send password link controls are gone.
// activates/deactivates them. No delete -- deactivation is a soft toggle that
// preserves job history.
//
// Session ALV: the Edit modal also carries the OPTIONAL link to an engineer's own row on the
// subcontractor register (ELE's engineers are engaged as subcontractors, not PAYE employees). It is
// a link, not a merge, and this page is where it is set and cleared; Compliance > Personnel >
// Engineer is where the office follows it through to the insurance, agreement and certificates.
//
// SESSION AUV, design system Session 8 (People): the existing ListToolbar gains its result count, the below-md sort and
// Clear; Active / Inactive and the calendar-feed state are StatusPill tags (the same green and grey as before); the
// primary buttons are the shared Button; Loader and EmptyState replace the bare loading line, the skeleton and the three
// message paragraphs; and the one date helper is lib/format.ts.
// The neutral outline buttons (Edit, Contact, Cost rate, Stock, Send app details, Deactivate, Delete) are left as
// they are: the Button component has no neutral variant. Presentation only.
import { useCallback, useEffect, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Plus } from "lucide-react";
import { ApiError, api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { SendAppDetailsButton } from "../../components/SendAppDetailsButton";
import {
  ENGINEER_NEEDS_DETAILS_NOTE,
  EMAIL_TAKEN_MESSAGE,
  sharesEmailNote,
  engineerEmailProblem,
  engineerMobileProblem,
  engineerNeedsDetails,
} from "../../lib/engineerContact";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import type { CalendarFeedSummary, EngineerAppDetailsResult, EngineerCalendarFeedRow, EngineerListItem, EngineerPayType, RateCardApprovedWeek, SubcontractorListItem, VanStockItem } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { ListToolbar } from "../../components/ListToolbar";
import { SortableHeader } from "../../components/SortableHeader";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { formatDate, formatDateLong } from "../../lib/format";
import { useAuth } from "../../lib/auth";
// Session ASB: "Sign out everywhere" for one engineer (ends their sessions, keeps the account). Admin only.
import { EngineerRecentEmailButton } from "./EngineerRecentEmail";
import { SignOutEverywhereButton } from "./SignOutEverywhereButton";
import { NotificationsLine, TestNotificationButton } from "./EngineerNotifications";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";

const emptyForm = { displayName: "", email: "", mobile: "" };

// M-10, Session AQU: the free-text search and the sortable columns. The office types a name, a
// TECH-NNN id, an email address or a mobile number. Status sorts by its label, so ascending
// puts Active before Inactive. This page lists Users with role engineer only, so the Director's
// admin-linked engineer row (Sessions AGW, AQP) is not in these rows and search cannot surface it.
const ENGINEER_SEARCH_ACCESSORS: SearchAccessors<EngineerListItem> = [
  (e) => e.displayName,
  (e) => e.techId,
  (e) => e.email,
  (e) => e.mobile,
];
// The below-md sort select (a card list has no header to click).
const SORT_OPTIONS = [
  { key: "name", label: "Name" },
  { key: "techId", label: "Engineer ID" },
  { key: "status", label: "Status" },
];
const ENGINEER_SORT_ACCESSORS: SortAccessors<EngineerListItem> = {
  techId: (e) => e.techId,
  name: (e) => e.displayName,
  email: (e) => e.email,
  status: (e) => (e.isActive ? "Active" : "Inactive"),
};

// Per-engineer van stock: a self-contained modal listing the engineer's parts with
// quantity +/- adjust, reorder highlighting, an add form and remove.
function StockButton({ engineer }: { engineer: EngineerListItem }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<VanStockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [add, setAdd] = useState({ partCode: "", description: "", quantity: "0", reorderThreshold: "1", unitCost: "" });

  async function openModal() {
    setOpen(true);
    setError(null);
    setLoading(true);
    try {
      setItems(await api.engineers.stock(engineer.id));
    } catch {
      setError("Could not load this engineer's stock.");
    } finally {
      setLoading(false);
    }
  }

  async function adjust(partCode: string, delta: number) {
    try {
      const updated = await api.engineers.updateStock(engineer.id, partCode, { delta });
      setItems((xs) => xs.map((x) => (x.partCode === partCode ? updated : x)));
    } catch {
      setError("Could not update that item.");
    }
  }

  async function remove(partCode: string) {
    // The delete is confirmed by the inline two-step ConfirmButton (Session AR Item 3).
    try {
      await api.engineers.removeStock(engineer.id, partCode);
      setItems((xs) => xs.filter((x) => x.partCode !== partCode));
    } catch {
      setError("Could not remove that item.");
    }
  }

  async function addItem() {
    if (!add.partCode.trim() || !add.description.trim()) {
      setError("Part code and description are required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.engineers.addStock(engineer.id, {
        partCode: add.partCode.trim(),
        description: add.description.trim(),
        quantity: Number(add.quantity) || 0,
        reorderThreshold: Number(add.reorderThreshold) || 0,
        unitCost: add.unitCost.trim() ? Number(add.unitCost) : undefined,
      });
      setItems((xs) => [...xs, created].sort((a, b) => a.partCode.localeCompare(b.partCode)));
      setAdd({ partCode: "", description: "", quantity: "0", reorderThreshold: "1", unitCost: "" });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? "That part code is already in this van." : "Could not add that item.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="rounded-ele border border-ele-blue px-2.5 py-1 text-xs text-ele-blue transition hover:bg-ele-blue/10"
      >
        Stock
      </button>
      {open && (
        <ModalShell ariaLabel="Van stock" onClose={() => !busy && setOpen(false)} width="max-w-lg" panelClassName="max-h-[90vh] overflow-y-auto p-6 text-left">
            <h2 className="mb-1 font-heading text-xl text-ele-strong">Van stock</h2>
            <p className="mb-4 text-sm text-ele-body">
              {engineer.displayName}
              {engineer.techId ? ` (${engineer.techId})` : ""}
            </p>

            {loading ? (
              <Loader label="Loading the van stock..." shape="inline" />
            ) : items.length === 0 ? (
              <p className="text-sm text-ele-body opacity-70">No stock items yet.</p>
            ) : (
              /* Session AGZ: a self-describing row list instead of a four-column table --
                 in the ~300px the modal offers a 375px phone, the part name, the +/-
                 stepper and Remove could not share a table row legibly. The controls wrap
                 under the part on a phone and sit inline at sm+; the steppers and Remove
                 clear 44px below sm (the office keeps its denser sizing at sm+). */
              <ul className="mb-4 divide-y divide-ele-purple-accent/40 border-y border-ele-purple-accent/40">
                {items.map((it) => (
                  <li key={it.id} className={`flex flex-wrap items-center gap-x-4 gap-y-1 py-2 ${it.needsReorder ? "text-amber-400" : "text-ele-body"}`}>
                    <div className="min-w-0 flex-1 basis-40">
                      <div className="text-ele-strong">{it.partCode}</div>
                      <div className="text-xs opacity-70">{it.description}</div>
                    </div>
                    <div className="flex flex-none flex-wrap items-center gap-x-4 gap-y-1">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => adjust(it.partCode, -1)}
                          aria-label={`Reduce ${it.partCode} quantity`}
                          className="rounded border border-ele-purple-accent px-1.5 leading-none max-sm:min-h-11 max-sm:min-w-11"
                        >
                          -
                        </button>
                        <span className="min-w-5 text-center text-ele-strong">{it.quantity}</span>
                        <button
                          type="button"
                          onClick={() => adjust(it.partCode, 1)}
                          aria-label={`Increase ${it.partCode} quantity`}
                          className="rounded border border-ele-purple-accent px-1.5 leading-none max-sm:min-h-11 max-sm:min-w-11"
                        >
                          +
                        </button>
                      </div>
                      <span className="text-xs opacity-70">Reorder at {it.reorderThreshold}</span>
                      <ConfirmButton
                        onConfirm={() => remove(it.partCode)}
                        label="Remove"
                        className="flex items-center text-xs text-ele-body hover:text-ele-danger max-sm:min-h-11"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mb-2 mt-2 font-menu text-xs uppercase tracking-wider text-ele-blue">Add an item</h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input value={add.partCode} onChange={(e) => setAdd((a) => ({ ...a, partCode: e.target.value }))} placeholder="Part code" className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green" />
              <input value={add.description} onChange={(e) => setAdd((a) => ({ ...a, description: e.target.value }))} placeholder="Description" className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green" />
              <input value={add.quantity} onChange={(e) => setAdd((a) => ({ ...a, quantity: e.target.value }))} type="number" min={0} placeholder="Quantity" className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green" />
              <input value={add.reorderThreshold} onChange={(e) => setAdd((a) => ({ ...a, reorderThreshold: e.target.value }))} type="number" min={0} placeholder="Reorder at" className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green" />
              <input value={add.unitCost} onChange={(e) => setAdd((a) => ({ ...a, unitCost: e.target.value }))} type="number" min={0} step="0.01" placeholder="Unit cost (optional)" className="sm:col-span-2 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green" />
            </div>
            {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              <button type="button" onClick={() => setOpen(false)} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}>Close</button>
              <Button onClick={addItem} disabled={busy} className={MODAL_ACTION_BTN}>{busy ? "Adding..." : "Add item"}</Button>
            </div>
        </ModalShell>
      )}
    </>
  );
}

// Edit an engineer's contact (email + mobile, used to send RFID tag URLs) and their next-of-kin
// fallback contact (Session AOU: the office's fallback if a lone-worker escalation call goes
// unanswered; the engineer normally sets this themselves in the PWA). Self-contained per-row
// action, in the same idiom as StockButton.
function ContactButton({ engineer, onSaved }: { engineer: EngineerListItem; onSaved: (u: EngineerListItem) => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(engineer.email ?? "");
  const [mobile, setMobile] = useState(engineer.mobile ?? "");
  const [nokName, setNokName] = useState(engineer.nextOfKinName ?? "");
  const [nokRelationship, setNokRelationship] = useState(engineer.nextOfKinRelationship ?? "");
  const [nokPhone, setNokPhone] = useState(engineer.nextOfKinPhone ?? "");
  const [homePostcode, setHomePostcode] = useState(engineer.homePostcode ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function openModal() {
    setEmail(engineer.email ?? "");
    setMobile(engineer.mobile ?? "");
    setNokName(engineer.nextOfKinName ?? "");
    setNokRelationship(engineer.nextOfKinRelationship ?? "");
    setNokPhone(engineer.nextOfKinPhone ?? "");
    setHomePostcode(engineer.homePostcode ?? "");
    setError(null);
    setOpen(true);
  }

  async function save() {
    // Session BCE: an engineer's email and mobile cannot be cleared (the emailed sign-in code goes to the email).
    const problem = engineerEmailProblem(email) ?? engineerMobileProblem(mobile);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await api.engineers.updateContact(engineer.id, {
        email: email.trim(),
        mobile: mobile.trim(),
        nextOfKinName: nokName.trim(),
        nextOfKinRelationship: nokRelationship.trim(),
        nextOfKinPhone: nokPhone.trim(),
        homePostcode: homePostcode.trim(),
      });
      onSaved(updated);
      setOpen(false);
    } catch (err) {
      setError(
        err instanceof ApiError && (err.details?.email?.[0] || err.details?.mobile?.[0] || err.details?.homePostcode?.[0])
          ? (err.details.email?.[0] ?? err.details.mobile?.[0] ?? err.details.homePostcode?.[0] ?? "Could not save the contact details.")
          : err instanceof ApiError && err.code === "email_taken"
            ? EMAIL_TAKEN_MESSAGE
            : "Could not save the contact details.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green"
      >
        Contact
      </button>
      {open && (
        <ModalShell ariaLabel="Contact details" onClose={() => !busy && setOpen(false)} width="max-w-md" panelClassName="p-6 text-left">
            <h2 className="mb-1 font-heading text-xl text-ele-strong">Contact details</h2>
            <p className="mb-4 text-sm text-ele-body">
              {engineer.displayName}
              {engineer.techId ? ` (${engineer.techId})` : ""}
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-contact-email">Email</label>
                <input
                  id="eng-contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-contact-mobile">Mobile</label>
                <input
                  id="eng-contact-mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                />
              </div>
              {/* Session BCW: a call-out's travel is measured from the nearest of the office and the engineers' homes. The
                  postcode is only used to work out that journey: no customer, engineer or chat ever sees it. */}
              <div>
                <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-contact-home-postcode">Home postcode (optional)</label>
                <input
                  id="eng-contact-home-postcode"
                  value={homePostcode}
                  onChange={(e) => setHomePostcode(e.target.value)}
                  placeholder="e.g. E17 7LG"
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                />
                <p className="mt-1 text-xs text-ele-body opacity-70">
                  Used only to work out call-out travel from the nearest of the office and the engineers&rsquo; homes. Never shown to a customer.
                </p>
              </div>
              {/* Session AOU: the office's fallback contact if a lone-worker escalation call to this
                  engineer goes unanswered (shown on the Lone Worker page's overdue row). Optional --
                  most engineers will not have one on file, and the engineer normally sets this
                  themselves in the PWA; this is the office's editable fallback. */}
              <div className="border-t border-ele-purple-accent/40 pt-3">
                <p className="mb-2 text-xs uppercase tracking-wide text-ele-body opacity-70">Next of kin (optional)</p>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-contact-nok-name">Their name</label>
                    <input
                      id="eng-contact-nok-name"
                      value={nokName}
                      onChange={(e) => setNokName(e.target.value)}
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-contact-nok-relationship">Relationship</label>
                    <input
                      id="eng-contact-nok-relationship"
                      value={nokRelationship}
                      onChange={(e) => setNokRelationship(e.target.value)}
                      placeholder="e.g. wife, brother, partner"
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-contact-nok-phone">Their phone number</label>
                    <input
                      id="eng-contact-nok-phone"
                      value={nokPhone}
                      onChange={(e) => setNokPhone(e.target.value)}
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                    />
                  </div>
                </div>
              </div>
              {error && <p role="alert" className="text-sm text-ele-danger">{error}</p>}
            </div>
            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              <button type="button" onClick={() => setOpen(false)} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}>
                Cancel
              </button>
              <Button onClick={save} disabled={busy} className={MODAL_ACTION_BTN}>
                {busy ? "Saving..." : "Save"}
              </Button>
            </div>
        </ModalShell>
      )}
    </>
  );
}

// Edit an engineer's details: name, contact email + mobile (Session AFW; both required since Session BCE, because the
// engineer signs in with a code emailed to the email and has no username or password), and the
// optional subcontractor-register link (Session ALV). The name was previously set
// only at create and never editable afterward. Self-contained per-row action, in the same idiom as
// ContactButton. An email already used by another account is 409 email_taken, shown inline.
//
// WHY THE SUBCONTRACTOR LINK LIVES HERE rather than as another per-row button: this row's action
// cell already carries eight controls and wraps at laptop widths, and "which record is this person
// on the subcontractor register" is an engineer DETAIL, which is exactly what this modal is for.
// It is also the setter AND the clearer in one control -- picking "Not linked" undoes a link made
// by mistake -- so the link satisfies the DELETE RULE without a second endpoint or a second dialog.
//
// A LINK CHANGE IS PREVIEWED AND CONFIRMED (AUU fix-up 2, 4 October 2026). Since Session AUU the pay check figure finds
// an engineer's rate card through this link, so changing it moves the figure of every week of theirs, APPROVED weeks
// included: a FIRST link prices them (unpriced to priced), a move between firms reprices them, an unlink unprices them.
// The server refuses such a change without the office's confirmation (409 approved_weeks_affected, the same guard the
// rate card editor has), and before this fix-up Save simply hit that refusal with no way through. Now, when the link
// in the form differs from the engineer's, Save first asks the route's own preview (it writes nothing); if it names no
// approved week the save goes ahead exactly as before; if it names some, they are shown with long dates and the office
// ticks a confirmation before Save sends affectsApprovedWeeks: true. Cancel sends nothing. A save with no link change
// never asks the preview and behaves exactly as it always did.
function EditButton({ engineer, onSaved }: { engineer: EngineerListItem; onSaved: (u: EngineerListItem) => void }) {
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState(engineer.displayName);
  const [email, setEmail] = useState(engineer.email ?? "");
  const [mobile, setMobile] = useState(engineer.mobile ?? "");
  const [subcontractorId, setSubcontractorId] = useState(engineer.subcontractorId ?? "");
  // Session RC1: a technician (office and admin) and how the engineer is paid.
  const [isTechnician, setIsTechnician] = useState(engineer.isTechnician ?? false);
  const [payType, setPayType] = useState<"" | EngineerPayType>(engineer.payType ?? "");
  // The directory to choose from, loaded when the modal OPENS rather than with the page: most edits
  // never touch this field, and an engineers page that cannot render until the subcontractor list
  // arrives would be a worse page for a list nobody asked for. null = still loading, [] = loaded
  // and genuinely empty, which the UI says out loud rather than showing an empty dropdown.
  const [subcontractors, setSubcontractors] = useState<SubcontractorListItem[] | null>(null);
  const [subLoadFailed, setSubLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  // The preview of a changed link that named approved weeks, held until the office confirms it or changes the link
  // again; null means no preview is waiting (no link change, or the preview named no week).
  const [linkPreview, setLinkPreview] = useState<{ to: string | null; weeks: RateCardApprovedWeek[] } | null>(null);
  const [linkConfirmed, setLinkConfirmed] = useState(false);

  const currentLink = engineer.subcontractorId ?? null;
  const wantedLink = subcontractorId || null;
  const linkChanged = wantedLink !== currentLink;

  function openModal() {
    setDisplayName(engineer.displayName);
    setEmail(engineer.email ?? "");
    setMobile(engineer.mobile ?? "");
    setSubcontractorId(engineer.subcontractorId ?? "");
    setIsTechnician(engineer.isTechnician ?? false);
    setPayType(engineer.payType ?? "");
    setError(null);
    setFieldErrors({});
    setLinkPreview(null);
    setLinkConfirmed(false);
    setSubcontractors(null);
    setSubLoadFailed(false);
    setOpen(true);
    // "all" so an ARCHIVED subcontractor is still selectable: archiving is ELE's normal
    // offboarding and keeps the record, so an engineer who has stopped subcontracting must not
    // silently lose the pointer to their own agreement, insurance and certificates. The default
    // list would exclude them and the currently-linked option would vanish from its own dropdown.
    api.subcontractors
      .list({ archived: "all" })
      .then(setSubcontractors)
      .catch(() => {
        setSubcontractors([]);
        setSubLoadFailed(true);
      });
  }

  async function save() {
    // Session BCE: email and mobile cannot be cleared; the emailed sign-in code goes to the email.
    const emailProblem = engineerEmailProblem(email);
    const mobileProblem = engineerMobileProblem(mobile);
    if (emailProblem || mobileProblem) {
      setFieldErrors({ ...(emailProblem ? { email: [emailProblem] } : {}), ...(mobileProblem ? { mobile: [mobileProblem] } : {}) });
      return;
    }
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      // A changed link is previewed first (AUU fix-up 2). The preview writes nothing; when it names an approved week the
      // save stops here and the office is shown the weeks and asked to confirm. A preview that cannot be read saves
      // nothing either: the office is never left guessing whether a figure would move.
      if (linkChanged && linkPreview === null) {
        const preview = await api.engineers.previewSubcontractorLink(engineer.id, wantedLink);
        if (preview.approvedWeeksMoved.length > 0) {
          setLinkPreview({ to: wantedLink, weeks: preview.approvedWeeksMoved });
          setLinkConfirmed(false);
          return;
        }
      }
      // The Save button is disabled until the confirmation is ticked, so this is a guard, not a path.
      if (linkPreview !== null && !linkConfirmed) return;
      const updated = await api.engineers.update(engineer.id, {
        displayName: displayName.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
        // "" clears the link (the server treats "" and null alike), so the "Not linked" option is
        // a real undo rather than a no-op. Sent on every save because this modal owns the field:
        // omitting it would make the control unable to clear anything.
        subcontractorId: wantedLink,
        // Session RC1: sent only when changed, so an older server and an untouched field behave as before.
        ...(isTechnician !== (engineer.isTechnician ?? false) ? { isTechnician } : {}),
        ...(payType !== (engineer.payType ?? "") ? { payType: payType || null } : {}),
        // The office's confirmation, in terms, and only after the preview showed them the weeks.
        ...(linkPreview !== null && linkConfirmed ? { affectsApprovedWeeks: true as const } : {}),
      });
      onSaved(updated);
      setOpen(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.body?.error === "approved_weeks_affected") {
        // A week was approved between the preview and the save: show the server's sentence and ask again.
        setError(err.message);
        setLinkPreview(null);
        setLinkConfirmed(false);
      } else if (err instanceof ApiError && err.code === "email_taken") setFieldErrors({ email: [EMAIL_TAKEN_MESSAGE] });
      else if (err instanceof ApiError && err.details) setFieldErrors(err.details);
      else if (err instanceof ApiError) setError(friendlyApiError(err, "Could not save the changes. Please try again."));
      else setError(linkChanged && linkPreview === null ? "Could not check whether this link change would reprice approved weeks. Nothing was saved. Please try again." : "Could not save the changes. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  // What the confirmation says, in the office's words: the kind of change, the firm, and each week with a long date.
  function linkChangeHeadline(weeks: RateCardApprovedWeek[], to: string | null): string {
    const n = weeks.length;
    const count = `${n} already approved week${n === 1 ? "" : "s"}`;
    const toName = to ? ((subcontractors ?? []).find((sc) => sc.id === to)?.company ?? "the selected subcontractor") : null;
    const fromName = engineer.subcontractorCompany ?? "their current subcontractor";
    if (currentLink === null) return `Linking ${engineer.displayName} to ${toName} changes the pay check figure of ${count} from unpriced to priced at that subcontractor's rate card:`;
    if (to === null) return `Removing ${engineer.displayName}'s link to ${fromName} changes the pay check figure of ${count} from priced to unpriced:`;
    return `Moving ${engineer.displayName} from ${fromName} to ${toName} changes the pay check figure of ${count} from one firm's rate card to the other's:`;
  }

  const fieldErr = (name: string) => fieldErrors[name]?.[0];

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green"
      >
        Edit
      </button>
      {open && (
        <ModalShell ariaLabel="Edit engineer" onClose={() => !busy && setOpen(false)} width="max-w-md" panelClassName="p-6 text-left">
          <h2 className="mb-1 font-heading text-xl text-ele-strong">Edit engineer</h2>
          <p className="mb-4 text-sm text-ele-body">{engineer.techId ?? "Engineer"}</p>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-edit-name">Name</label>
              <input
                id="eng-edit-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
              />
              {fieldErr("displayName") && <p role="alert" className="mt-1 text-sm text-ele-danger">{fieldErr("displayName")}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-edit-email">Email (required)</label>
              <input
                id="eng-edit-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
              />
              {fieldErr("email") && <p role="alert" className="mt-1 text-sm text-ele-danger">{fieldErr("email")}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-edit-mobile">Mobile (required)</label>
              <input
                id="eng-edit-mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
              />
              {fieldErr("mobile") && <p role="alert" className="mt-1 text-sm text-ele-danger">{fieldErr("mobile")}</p>}
            </div>
            {/* Session ALV: the optional link to this engineer's own subcontractor record. A LINK,
                never a merge: nothing about the subcontractor is copied onto the engineer, and
                nothing here changes what an unlinked engineer does. */}
            <div>
              <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-edit-subcontractor">Subcontractor record</label>
              <select
                id="eng-edit-subcontractor"
                value={subcontractorId}
                disabled={subcontractors === null}
                onChange={(e) => {
                  setSubcontractorId(e.target.value);
                  // A preview describes one change; a different pick needs its own.
                  setLinkPreview(null);
                  setLinkConfirmed(false);
                }}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-60"
              >
                <option value="">Not linked</option>
                {/* The currently-linked record is offered even while the directory is still
                    loading, and again if the load FAILED, so opening this modal on a bad
                    connection and pressing Save cannot silently clear a link the office never
                    touched. Skipped once the real list arrives holding it, to avoid a duplicate. */}
                {engineer.subcontractorId && !(subcontractors ?? []).some((sc) => sc.id === engineer.subcontractorId) ? (
                  <option value={engineer.subcontractorId}>{engineer.subcontractorCompany ?? "Currently linked record"}</option>
                ) : null}
                {(subcontractors ?? []).map((sc) => (
                  <option key={sc.id} value={sc.id}>
                    {sc.company}
                    {sc.archivedAt ? " (archived)" : ""}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ele-body opacity-70">
                {subcontractors === null
                  ? "Loading the subcontractor register..."
                  : subLoadFailed
                    ? "Could not load the subcontractor register just now, so only the current link is offered. Close and reopen to try again."
                    : subcontractors.length === 0
                      ? "No subcontractors on the register yet. Add one under People, then Subcontractors, then link it here."
                      : "Where this engineer's insurance, signed agreement, competency certificates and DBS check are held. Leave as Not linked if they have no subcontractor record."}
              </p>
              {fieldErr("subcontractorId") && <p role="alert" className="mt-1 text-sm text-ele-danger">{fieldErr("subcontractorId")}</p>}
            </div>
            {/* Session RC1: a technician is booked on technician visits; the pay type says whether a card prices them. */}
            <div>
              <label className="flex items-center gap-2 text-sm text-ele-strong max-sm:min-h-11">
                <input type="checkbox" checked={isTechnician} onChange={(e) => setIsTechnician(e.target.checked)} />
                Technician
              </label>
              <p className="mt-1 text-xs text-ele-body opacity-70">Only a technician can be booked on a technician visit.</p>
            </div>
            <div>
              <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-edit-paytype">Pay type</label>
              <select
                id="eng-edit-paytype"
                value={payType}
                onChange={(e) => setPayType(e.target.value as "" | EngineerPayType)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
              >
                <option value="">Not set</option>
                <option value="SUBCONTRACTOR">Subcontractor</option>
                <option value="PAYE">PAYE</option>
              </select>
              {payType === "PAYE" && wantedLink && (
                <p className="mt-1 text-xs text-ele-body">A PAYE engineer is paid through payroll and is never linked to a subcontractor. Set the subcontractor record to Not linked.</p>
              )}
            </div>
            {/* AUU fix-up 2: the preview named approved weeks, so the office confirms before Save sends the change. */}
            {linkPreview && (
              <div role="status" className="rounded-ele border border-ele-purple-accent px-3 py-2 text-sm">
                <p className="text-ele-strong">{linkChangeHeadline(linkPreview.weeks, linkPreview.to)}</p>
                <ul className="mt-1 list-disc pl-5 text-ele-body">
                  {linkPreview.weeks.map((w) => (
                    <li key={`${w.engineerId}-${w.weekStarting}`}>week beginning {formatDateLong(w.weekStarting)}</li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-ele-body opacity-70">An approved week's check figure is derived live, so it would move. Nothing has been saved yet.</p>
                <label className="mt-2 flex items-center gap-2 text-ele-body">
                  <input type="checkbox" checked={linkConfirmed} onChange={(e) => setLinkConfirmed(e.target.checked)} />
                  Yes, change the check figure of {linkPreview.weeks.length === 1 ? "that approved week" : "those approved weeks"}. The change is recorded against my name.
                </label>
              </div>
            )}
            {error && <p role="alert" className="text-sm text-ele-danger">{error}</p>}
          </div>
          <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
            <button type="button" onClick={() => setOpen(false)} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}>
              Cancel
            </button>
            <Button onClick={save} disabled={busy || (linkPreview !== null && !linkConfirmed)} className={MODAL_ACTION_BTN}>
              {busy ? "Saving..." : linkPreview ? "Confirm and save" : "Save"}
            </Button>
          </div>
        </ModalShell>
      )}
    </>
  );
}

// Session RC1: the PAYE figures as the admin types them. Pounds a year to pence, hours a week to minutes; blank clears.
export function payeFormFrom(e: EngineerListItem): { salary: string; hours: string; startDate: string } {
  return {
    salary: e.payeSalaryPence == null ? "" : String(e.payeSalaryPence / 100),
    hours: e.payeContractedWeeklyMins == null ? "" : String(e.payeContractedWeeklyMins / 60),
    startDate: e.payeStartDate ?? "",
  };
}
export function payeBodyFromForm(f: { salary: string; hours: string; startDate: string }):
  | { body: { payeSalaryPence: number | null; payeContractedWeeklyMins: number | null; payeStartDate: string | null } }
  | { error: string } {
  const salaryText = f.salary.trim();
  const salary = salaryText === "" ? null : Math.round(Number(salaryText) * 100);
  if (salary != null && (!Number.isFinite(salary) || salary < 0)) return { error: "Enter the salary in pounds a year, or leave it blank." };
  const hoursText = f.hours.trim();
  const mins = hoursText === "" ? null : Math.round(Number(hoursText) * 60);
  if (mins != null && (!Number.isFinite(mins) || mins < 60 || mins > 10_080)) return { error: "Enter the contracted hours a week, for example 40, or leave it blank." };
  const start = f.startDate.trim();
  if (start !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(start)) return { error: "Enter the start date, or leave it blank." };
  return { body: { payeSalaryPence: salary, payeContractedWeeklyMins: mins, payeStartDate: start === "" ? null : start } };
}

// Edit an engineer's internal labour cost rate (pounds/hour, ex VAT), used for job-cost margin
// (Session AK Item 2). Admin only (the whole page is). Self-contained per-row action, in the same
// idiom as ContactButton. The rate is never customer-facing.
function CostRateButton({ engineer, onSaved }: { engineer: EngineerListItem; onSaved: (u: EngineerListItem) => void }) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(engineer.costRatePerHour != null ? String(engineer.costRatePerHour) : "");
  // Session RC1: the PAYE figures, admin only, beside the cost rate. Pounds a year (stored as pence), hours a week (stored
  // as minutes), the start date. Shown only for a PAYE engineer.
  const isPaye = engineer.payType === "PAYE";
  const [paye, setPaye] = useState(() => payeFormFrom(engineer));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function openModal() {
    setRate(engineer.costRatePerHour != null ? String(engineer.costRatePerHour) : "");
    setPaye(payeFormFrom(engineer));
    setError(null);
    setOpen(true);
  }

  async function save() {
    const trimmed = rate.trim();
    let value: number | null;
    if (trimmed === "") {
      value = null; // clears the rate
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) {
        setError("Enter a cost rate of 0 or more, or leave blank to clear it.");
        return;
      }
      value = n;
    }
    let payeBody: ReturnType<typeof payeBodyFromForm> | null = null;
    if (isPaye) {
      payeBody = payeBodyFromForm(paye);
      if ("error" in payeBody) {
        setError(payeBody.error);
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      let updated = await api.engineers.updateCostRate(engineer.id, value);
      if (payeBody && "body" in payeBody) updated = await api.engineers.update(engineer.id, payeBody.body);
      onSaved(updated);
      setOpen(false);
    } catch {
      setError("Could not save the cost rate.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green"
      >
        Cost rate
      </button>
      {open && (
        <ModalShell ariaLabel="Cost rate per hour" onClose={() => !busy && setOpen(false)} width="max-w-md" panelClassName="p-6 text-left">
            <h2 className="mb-1 font-heading text-xl text-ele-strong">Cost rate per hour</h2>
            <p className="mb-4 text-sm text-ele-body">
              {engineer.displayName}
              {engineer.techId ? ` (${engineer.techId})` : ""}
            </p>
            <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-cost-rate">Cost rate per hour (GBP, ex VAT)</label>
            <input
              id="eng-cost-rate"
              type="number"
              min={0}
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g. 35.00"
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />
            <p className="mt-1 text-xs text-ele-body opacity-70">
              Used to work out the labour cost and margin on jobs. Internal only, never shown to customers. Leave blank to clear.
            </p>
            {isPaye && (
              <div className="mt-4 space-y-3" aria-label="PAYE figures">
                <p className="text-xs text-ele-body">Loaded cost per hour, for job profitability.</p>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-paye-salary">Salary (GBP a year)</label>
                  <input id="eng-paye-salary" type="number" min={0} step="1" value={paye.salary} onChange={(e) => setPaye((p) => ({ ...p, salary: e.target.value }))} placeholder="e.g. 45000" className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green" />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-paye-hours">Contracted hours a week</label>
                  <input id="eng-paye-hours" type="number" min={1} step="0.25" value={paye.hours} onChange={(e) => setPaye((p) => ({ ...p, hours: e.target.value }))} placeholder="e.g. 40" className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green" />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="eng-paye-start">Start date</label>
                  <input id="eng-paye-start" type="date" value={paye.startDate} onChange={(e) => setPaye((p) => ({ ...p, startDate: e.target.value }))} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green" />
                </div>
              </div>
            )}
            {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              <button type="button" onClick={() => setOpen(false)} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}>
                Cancel
              </button>
              <Button onClick={save} disabled={busy} className={MODAL_ACTION_BTN}>
                {busy ? "Saving..." : "Save"}
              </Button>
            </div>
        </ModalShell>
      )}
    </>
  );
}

// ---- Calendar feed (Session ATY) ----
// An engineer's private calendar link (their own jobs in their own calendar app, made from the engineer app). The office
// sees whether it is on, can revoke it (a lost phone, a link shared by mistake) and can delete the record of a link that
// is already switched off (RULE 1). The link itself never reaches this page: the server stores only a hash of it.
// Rows are keyed by the USER id on this page, so a feed is matched to its row by userId.
// The pill and the controls, inside the row's desktop action cell. Text here stays short: the cell does not wrap.
function CalendarFeedCell({
  row,
  engineerName,
  onRevoked,
  onOpenHistory,
}: {
  row: EngineerCalendarFeedRow;
  engineerName: string;
  onRevoked: () => void;
  onOpenHistory: (row: EngineerCalendarFeedRow) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke() {
    setBusy(true);
    setError(null);
    try {
      await api.engineers.revokeCalendarFeed(row.engineerId);
      onRevoked();
    } catch {
      setError("Could not revoke. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {row.live ? (
        <span className="self-center" data-testid="calendar-feed-pill">
          <StatusPill word={`Calendar feed: On · since ${formatDate(row.live.createdAt)}`} tone="green" />
        </span>
      ) : (
        <span className="self-center" data-testid="calendar-feed-pill">
          <StatusPill word="Calendar feed: Off" tone="grey" />
        </span>
      )}
      {row.live && (
        <ConfirmButton
          onConfirm={() => void revoke()}
          label="Revoke calendar link"
          confirmLabel={`Revoke ${engineerName}'s calendar link? Their calendar app stops getting jobs.`}
          busy={busy}
          busyLabel="Revoking..."
          resetMs={6000}
          className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-danger hover:text-ele-strong disabled:opacity-50"
        />
      )}
      {row.history.length > 0 && (
        <button
          type="button"
          onClick={() => onOpenHistory(row)}
          className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green"
        >
          Old calendar links ({row.history.length})
        </button>
      )}
      {error && (
        <span role="alert" className="self-center text-xs text-ele-danger">
          {error}
        </span>
      )}
    </>
  );
}

export function EngineersPage() {
  // Session ASB: "Sign out everywhere" is an admin action (the server refuses anyone else).
  const isAdmin = useAuth().user?.role === "admin";
  const [engineers, setEngineers] = useState<EngineerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  // jsdom has no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<EngineerListItem | null>(null);
  const [createdAppDetails, setCreatedAppDetails] = useState<EngineerAppDetailsResult | null>(null);

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<EngineerListItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setListError(false);
    try {
      setEngineers(await api.engineers.list());
    } catch {
      setListError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Session ATY: each engineer's calendar feed, keyed by USER id (this page's row key). Loaded beside the list and
  // NON-BLOCKING: a failure leaves the page exactly as it was, with no feed information.
  const [feedsByUser, setFeedsByUser] = useState<Map<string, EngineerCalendarFeedRow>>(() => new Map());
  const [feedHistoryRow, setFeedHistoryRow] = useState<EngineerCalendarFeedRow | null>(null);
  const [confirmFeedDelete, setConfirmFeedDelete] = useState<{ feed: CalendarFeedSummary; engineerName: string } | null>(null);
  const [feedDeleteBusy, setFeedDeleteBusy] = useState(false);
  const [feedDeleteError, setFeedDeleteError] = useState<string | null>(null);

  const loadFeeds = useCallback(async () => {
    try {
      const res = await api.engineers.calendarFeeds();
      const byUser = new Map<string, EngineerCalendarFeedRow>();
      for (const row of res?.engineers ?? []) if (row.userId) byUser.set(row.userId, row);
      setFeedsByUser(byUser);
    } catch {
      // Non-blocking by design: the Engineers list renders without feed information.
    }
  }, []);

  useEffect(() => {
    void loadFeeds();
  }, [loadFeeds]);

  async function doFeedDelete() {
    if (!confirmFeedDelete) return;
    setFeedDeleteBusy(true);
    setFeedDeleteError(null);
    try {
      await api.engineers.deleteCalendarFeed(confirmFeedDelete.feed.id);
      setConfirmFeedDelete(null);
      await loadFeeds();
    } catch (err) {
      // 409 feed_live (the link is still on) carries the server's own sentence.
      setFeedDeleteError(friendlyApiError(err, "Could not delete that record. Please try again."));
    } finally {
      setFeedDeleteBusy(false);
    }
  }

  const set = (name: keyof typeof emptyForm) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [name]: e.target.value }));

  function openForm() {
    setForm({ ...emptyForm });
    setFieldErrors({});
    setFormError(null);
    setCreated(null);
    setCreatedAppDetails(null);
    setShowForm(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      // Session BCE: email and mobile are both required, so say so before the round trip. The server is the
      // authority and answers 400 with details.email / details.mobile, shown in the same places.
      const emailProblem = engineerEmailProblem(form.email);
      const mobileProblem = engineerMobileProblem(form.mobile);
      if (emailProblem || mobileProblem) {
        setFieldErrors({ ...(emailProblem ? { email: [emailProblem] } : {}), ...(mobileProblem ? { mobile: [mobileProblem] } : {}) });
        setSaving(false);
        return;
      }
      const eng = await api.engineers.create({
        displayName: form.displayName.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
      });
      setCreated(eng);
      setCreatedAppDetails(eng.appDetails ?? null);
      setShowForm(false);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === "email_taken") setFieldErrors({ email: [EMAIL_TAKEN_MESSAGE] });
      else if (err instanceof ApiError && err.details) setFieldErrors(err.details);
      else if (err instanceof ApiError) setFormError(friendlyApiError(err, "Could not create the engineer. Please try again."));
      else setFormError("Could not create the engineer. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // Session ANK: a reactivate that would produce a SECOND active engineer of the same name is
  // refused 409, and the office is offered the deliberate override. Held here rather than in
  // toggleError because it needs the engineer as well as the sentence: the confirm has to re-issue
  // the same call with the acknowledgement, and it must name who it is about.
  const [duplicateNameBlock, setDuplicateNameBlock] = useState<{ engineer: EngineerListItem; message: string } | null>(null);

  async function toggle(eng: EngineerListItem, acknowledgeDuplicateName?: boolean) {
    setTogglingId(eng.id);
    setToggleError(null);
    setDuplicateNameBlock(null);
    try {
      const updated = await api.engineers.setActive(eng.id, !eng.isActive, acknowledgeDuplicateName);
      setEngineers((es) => es.map((x) => (x.id === eng.id ? updated : x)));
    } catch (err) {
      // The refusal carries the reason and the account it clashes with, and that account may be on
      // no other screen (the admin-linked Adrian Baker row is invisible on this page, which lists
      // Users with role engineer). Swallowing it into "Please try again" would give advice that can
      // only ever fail: this refusal is permanent until something changes.
      if (err instanceof ApiError && err.code === "duplicate_active_engineer_name") {
        const message = typeof err.body?.message === "string" ? err.body.message : "Another active engineer already has that name.";
        setDuplicateNameBlock({ engineer: eng, message });
      } else if (err instanceof ApiError) {
        setToggleError(friendlyApiError(err, "Could not update that engineer. Please try again."));
      } else {
        setToggleError("Could not update that engineer. Please try again.");
      }
    } finally {
      setTogglingId(null);
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    const eng = confirmDelete;
    setDeletingId(eng.id);
    setDeleteError(null);
    try {
      await api.engineers.remove(eng.id);
      setEngineers((es) => es.filter((x) => x.id !== eng.id));
      setConfirmDelete(null);
    } catch (err) {
      // apiRequest surfaces the server's friendly message (e.g. the job-history block)
      // as the ApiError code, so it reads correctly straight to the office.
      setDeleteError(friendlyApiError(err, "Could not delete that engineer. Please try again."));
    } finally {
      setDeletingId(null);
    }
  }

  const canCreate = form.displayName.trim() && form.email.trim() && form.mobile.trim() && !saving;

  // M-10, Session AQU: one search and one sort, applied to BOTH layouts (the desktop table and the
  // mobile cards) so they always agree. No active sort column keeps the server's load order.
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("engineers");
  const rows = sortRows(filterRows(engineers, search, ENGINEER_SEARCH_ACCESSORS), ENGINEER_SORT_ACCESSORS, sort);

  return (
    <div>
      <PageHeader
        title="Engineers"
        className="mb-4"
        subtitle="Create and manage who can sign in to the engineer app. Engineers sign in with their email and an emailed code."
        actions={
          !showForm ? (
            <Button onClick={openForm} icon={<Plus aria-hidden size={15} />}>
              Add engineer
            </Button>
          ) : undefined
        }
      />

      {created && (
        <div role="status" className="mb-6 rounded-ele border border-ele-green bg-ele-surface p-5">
          <h2 className="font-heading text-lg text-ele-green">Engineer {created.techId} created</h2>
          <p className="mt-1 text-sm text-ele-body">
            {created.displayName} signs in to the engineer app with their email and a 6-digit code we email to it. There is no
            password to pass on.
          </p>
          {createdAppDetails && (
            <p className="mt-2 text-sm text-ele-body">
              {createdAppDetails.drafted
                ? `A draft with the app details is waiting in Gmail for ${createdAppDetails.to}. Open Gmail and press Send.`
                : createdAppDetails.message || "The app details draft could not be made. Use Send app details on their row."}
            </p>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-6 max-w-lg rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
          <h2 className="mb-4 font-heading text-xl text-ele-blue">New engineer</h2>
          <div className="grid gap-4">
            <Field label="Display name" name="displayName" value={form.displayName} onChange={set("displayName")} errors={fieldErrors.displayName} required />
            <Field label="Email" name="email" type="email" value={form.email} onChange={set("email")} errors={fieldErrors.email} required hint="They sign in with a 6-digit code emailed here. There is no password." />
            <Field label="Mobile" name="mobile" value={form.mobile} onChange={set("mobile")} errors={fieldErrors.mobile} required hint="A UK mobile, such as 07700 900123. Also used for the WhatsApp send option." />
          </div>
          {formError && (
            <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {formError}
            </p>
          )}
          <div className="mt-5 flex gap-3">
            <Button type="submit" disabled={!canCreate}>
              {saving ? "Creating..." : "Create engineer"}
            </Button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-ele border border-ele-purple-accent px-4 py-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      {toggleError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {toggleError}
        </p>
      )}

      {/* Session ANK: the duplicate-active-name refusal, with the override behind a two-step
          ConfirmButton (the PLATFORM TWO-STEP / BRANDED CONFIRM rule -- reactivating into a
          duplicate is consequential and outward-facing: both engineers then receive every
          entrapment and SLA text separately). Amber, not red: this is advisory, and the office may
          legitimately have two people of the same name.
          The amber classes are the ConfirmDeleteModal warning pattern verbatim, including
          --ele-amber-text-soft rather than flat amber: this page renders on the office shell's
          DARK surface, where flat #b8860b as small text fails WCAG AA (the Session ANF finding). */}
      {duplicateNameBlock && (
        <div role="alert" className="mb-3 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text-soft)]">
          <p>{duplicateNameBlock.message}</p>
          <div className="mt-2 flex items-center gap-3">
            <ConfirmButton
              label={`Reactivate ${duplicateNameBlock.engineer.displayName} anyway`}
              confirmLabel="Press once more to confirm"
              onConfirm={() => void toggle(duplicateNameBlock.engineer, true)}
              // ConfirmButton's className defaults to "", so a caller that passes none renders a
              // bare unstyled button. This matches the page's other secondary actions.
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-strong hover:border-ele-green"
            />
            <button
              type="button"
              className="font-menu text-xs uppercase tracking-wide text-ele-body hover:text-ele-green max-sm:min-h-11"
              onClick={() => setDuplicateNameBlock(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Only show the full-page loader on the FIRST load (no rows yet). A later refetch (e.g.
          after creating an engineer) keeps the populated table mounted, so an in-progress SendLogin
          60-second freeze is not silently discarded by an unmount (Session AGB review). */}
      {!listError && engineers.length > 0 && (
        <ListToolbar
          className="mb-4"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search name, engineer ID, email or mobile..."
          searchLabel="Search engineers"
          resultCount={{ shown: rows.length, total: engineers.length, noun: "engineers" }}
          sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}

      {loading && engineers.length === 0 ? (
        <Loader label="Loading the engineers..." rows={4} />
      ) : listError ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the engineers. Please try again in a moment.
        </p>
      ) : engineers.length === 0 ? (
        <EmptyState message={'No engineers yet. Use "Add engineer" to create the first sign-in.'} />
      ) : rows.length === 0 ? (
        <EmptyState role="status" message={`No engineers match "${search.trim()}".`} />
      ) : mobileCards ? (
        /* Session ALY mobile card list. Below md the fixed-layout table redistributes its three
           surviving columns (Name 22%, Status 14%, Actions 34% of ~70%) so Name gets roughly
           86px of content room -- enough for a short name but not a longer one, and the same
           division-of-a-narrow-strip bug fixed elsewhere. The card stacks instead: Name never
           truncates, and Engineer ID / Username -- both desktop-only columns before this -- come
           back as a second and third line. Actions keeps only Deactivate/Reactivate, matching
           this page's own existing, explicitly-reasoned mobile design ("the single most
           important action... Contact, Cost rate, Stock and the permanent Delete are
           desktop-only on a small screen"): the card fixes the truncation bug without silently
           reversing that call. Edit/Contact/Cost rate/Stock/Send app details/Delete
           remain reachable on tablet and desktop widths exactly as today. */
        <MobileCardList label="Engineers">
          {rows.map((eng) => (
            <MobileListCard
              key={eng.id}
              primary={eng.displayName}
              secondary={eng.techId ?? undefined}
              meta={engineerNeedsDetails(eng) ? ENGINEER_NEEDS_DETAILS_NOTE : (eng.email ?? undefined)}
              right={
                <StatusPill word={eng.isActive ? "Active" : "Inactive"} tone={eng.isActive ? "green" : "grey"} />
              }
              actions={
                <button
                  type="button"
                  onClick={() => toggle(eng)}
                  disabled={togglingId === eng.id || deletingId === eng.id}
                  className="flex min-h-11 items-center rounded-ele border border-ele-purple-accent px-3 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50"
                >
                  {togglingId === eng.id ? "Saving..." : eng.isActive ? "Deactivate" : "Reactivate"}
                </button>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            {/* The action column carries five controls; widened (and the button row wraps below) so
                the buttons never overflow left into the Status cell (Session AR Item 16).
                No detail page: below md the priority is Name (the human-readable identifier,
                over the TECH-NNN id) + Status + the single most important action
                (Deactivate/Reactivate, which is reversible; Contact, Cost rate, Stock and the
                permanent Delete are desktop-only on a small screen, rule 4). */}
            <colgroup>
              <col className="max-md:hidden" style={{ width: "14%" }} />
              <col style={{ width: "22%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "34%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Engineer ID" colKey="techId" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Name" colKey="name" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Email" colKey="email" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                <th className="cell-actions px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((eng) => (
                <tr key={eng.id} className={`border-t border-ele-purple-accent ${eng.isActive ? "" : "opacity-60"}`}>
                  <td className="max-md:hidden px-3 py-2 align-top font-heading text-ele-strong">{eng.techId ?? "-"}</td>
                  <td className="px-3 py-2 align-top">
                    {eng.displayName}
                    {engineerNeedsDetails(eng) ? (
                      <p role="status" className="mt-1 text-xs text-[var(--ele-amber-text-soft)]">{ENGINEER_NEEDS_DETAILS_NOTE}</p>
                    ) : null}
                  </td>
                  <td className="max-md:hidden px-3 py-2 align-top break-words">
                    {eng.email ?? "-"}
                    {sharesEmailNote(eng.sharesEmailWith) ? (
                      <p className="mt-1 text-xs text-ele-body">{sharesEmailNote(eng.sharesEmailWith)}</p>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <StatusPill word={eng.isActive ? "Active" : "Inactive"} tone={eng.isActive ? "green" : "grey"} />
                    {/* Session PN1: can a new job reach them? On with the number of phones, off, or never set up. */}
                    <NotificationsLine engineer={eng} />
                  </td>
                  <td className="cell-actions px-3 py-2 text-right align-top">
                    <div className="flex flex-wrap justify-end gap-2">
                      <div className="contents max-md:hidden">
                        <EditButton
                          engineer={eng}
                          onSaved={(updated) => setEngineers((es) => es.map((x) => (x.id === updated.id ? updated : x)))}
                        />
                        <ContactButton
                          engineer={eng}
                          onSaved={(updated) => setEngineers((es) => es.map((x) => (x.id === updated.id ? updated : x)))}
                        />
                        {isAdmin ? (
                          <CostRateButton
                            engineer={eng}
                            onSaved={(updated) => setEngineers((es) => es.map((x) => (x.id === updated.id ? updated : x)))}
                          />
                        ) : null}
                        <StockButton engineer={eng} />
                        <EngineerRecentEmailButton userId={eng.id} name={eng.displayName} />
                        {/* Session BCE: leave the engineer the app details (what it is, where to install it, that they
                            sign in with their email and an emailed code) as a Gmail draft. Needs an email on record;
                            the amber note on the row says when it is missing. */}
                        {eng.isActive && eng.email ? <SendAppDetailsButton userId={eng.id} name={eng.displayName} /> : null}
                        {isAdmin && eng.isActive ? <SignOutEverywhereButton userId={eng.id} name={eng.displayName} /> : null}
                        <TestNotificationButton engineer={eng} />
                        {/* Session ATY: the engineer's calendar feed. Nothing shows for an engineer who never had one. */}
                        {feedsByUser.has(eng.id) ? (
                          <CalendarFeedCell
                            row={feedsByUser.get(eng.id)!}
                            engineerName={eng.displayName}
                            onRevoked={() => void loadFeeds()}
                            onOpenHistory={(row) => setFeedHistoryRow(row)}
                          />
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggle(eng)}
                        disabled={togglingId === eng.id || deletingId === eng.id}
                        className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50"
                      >
                        {togglingId === eng.id ? "Saving..." : eng.isActive ? "Deactivate" : "Reactivate"}
                      </button>
                      <div className="contents max-md:hidden">
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteError(null);
                            setConfirmDelete(eng);
                          }}
                          disabled={togglingId === eng.id || deletingId === eng.id}
                          className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-danger hover:text-ele-danger disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Session ACH: the shared ConfirmDeleteModal's destructive variant requires a two-step
          confirmation (arm, then confirm). Session AFW: the delete WORKS with completed job history
          (open jobs return to the pool, completed jobs are kept, and the sign-in stops immediately).
          Session AGA: timesheet (payroll) records no longer block it either -- the engineer's weeks
          are retained with the engineer reference dropped, so there is no remaining server block. */}
      <ConfirmDeleteModal
        open={!!confirmDelete}
        title="Delete this engineer?"
        message={
          confirmDelete
            ? `${confirmDelete.displayName}${confirmDelete.techId ? ` (${confirmDelete.techId})` : ""}. This permanently removes the sign-in, the engineer record, their van stock and their certifications, and cannot be undone. Their open jobs return to the unassigned pool, which means they show on no engineer's phone until you assign them to someone else; completed jobs are kept, and past timesheet weeks are retained as payroll records with the engineer reference removed.`
            : ""
        }
        confirmLabel="Delete engineer"
        variant="destructive"
        busy={!!confirmDelete && deletingId === confirmDelete.id}
        error={deleteError}
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* Session ATY: an engineer's switched-off calendar links. Portaled by ModalShell, so its text wraps (a list
          row's cell does not). Each record is deleted through the page's two-step ConfirmDeleteModal below. */}
      {feedHistoryRow && (() => {
        const name = engineers.find((e) => e.id === feedHistoryRow.userId)?.displayName ?? feedHistoryRow.engineerName;
        return (
          <ModalShell ariaLabel={`Old calendar links for ${name}`} onClose={() => setFeedHistoryRow(null)} closeOnBackdrop>
            <h2 className="font-heading text-lg text-ele-blue">Old calendar links</h2>
            <p className="mt-2 text-sm text-ele-body">
              {name}&apos;s switched-off calendar links. None of them works any more. Deleting a record removes it from this list
              for good.
            </p>
            <ul className="mt-4 space-y-2">
              {feedHistoryRow.history.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent p-3 text-sm">
                  <span className="text-ele-body">
                    Made {formatDate(f.createdAt)}.
                    {f.revokedAt ? ` Switched off ${formatDate(f.revokedAt)}${f.revokedByName ? ` by ${f.revokedByName}` : ""}.` : ""}
                    {f.lastFetchedAt ? ` Last read ${formatDate(f.lastFetchedAt)}.` : " Never read by a calendar app."}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setFeedHistoryRow(null);
                      setFeedDeleteError(null);
                      setConfirmFeedDelete({ feed: f, engineerName: name });
                    }}
                    className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-danger hover:text-ele-danger max-sm:min-h-11"
                  >
                    Delete record
                  </button>
                </li>
              ))}
            </ul>
            <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
              <button
                type="button"
                onClick={() => setFeedHistoryRow(null)}
                className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}
              >
                Close
              </button>
            </div>
          </ModalShell>
        );
      })()}

      <ConfirmDeleteModal
        open={!!confirmFeedDelete}
        title="Delete this calendar link record?"
        message={
          confirmFeedDelete
            ? `The switched-off calendar link ${confirmFeedDelete.engineerName} made on ${formatDate(confirmFeedDelete.feed.createdAt)}. The link already stopped working when it was switched off; this removes the record of it for good.`
            : ""
        }
        confirmLabel="Delete record"
        variant="destructive"
        busy={feedDeleteBusy}
        error={feedDeleteError}
        onConfirm={() => void doFeedDelete()}
        onCancel={() => setConfirmFeedDelete(null)}
      />
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  errors,
  required,
  hint,
  type = "text",
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  errors?: string[];
  required?: boolean;
  hint?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm" htmlFor={name}>
        {label}
        {required && <span className="text-ele-green"> *</span>}
      </label>
      <input
        id={name}
        type={type}
        autoComplete={type === "password" ? "new-password" : "off"}
        autoCapitalize={name === "username" ? "none" : undefined}
        value={value}
        onChange={onChange}
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
      />
      {hint && <p className="mt-1 text-xs text-ele-body opacity-70">{hint}</p>}
      {errors?.map((msg) => (
        <p key={msg} role="alert" className="mt-1 text-xs text-ele-strong">
          {msg}
        </p>
      ))}
    </div>
  );
}
